import React, { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Segmented } from '../components/ui/segmented';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { adminService, ordersService } from '../services';

const StatCard = ({ title, value, sub, trend, icon, color }: { title: string; value: string | number; sub?: string; trend?: string; icon: React.ReactNode; color: string }) => (
  <div className="flex-1 min-w-[240px] rounded-xl border border-gray-200 dark:border-gray-800 p-6 bg-white dark:bg-gray-900 hover:shadow-lg transition-all duration-200 group">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">{title}</p>
        <p className="mt-2 text-3xl font-bold leading-tight text-gray-900 dark:text-gray-100 group-hover:scale-105 transition-transform duration-200">{value}</p>
        {sub && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{sub}</p>}
      </div>
      <div className="text-3xl opacity-80 group-hover:opacity-100 transition-opacity duration-200" style={{ color }}>{icon}</div>
    </div>
    {trend && (
      <div className="mt-4">
        <Badge className={`${trend.startsWith('+') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} font-medium`}>
          {trend}
        </Badge>
      </div>
    )}
  </div>
);

const WelcomeBanner = ({ user }: { user: any }) => {
  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const getUserRoleDisplay = () => {
    const userRole = user?.roleName || user?.role?.name || null;
    
    switch (userRole) {
      case 'super_admin': return 'Super Administrator';
      case 'admin': return 'Administrator';
      case 'partner': return 'Partner';
      case 'user': return 'User';
      default: return 'User';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-8 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-950/30 dark:via-indigo-950/30 dark:to-purple-950/30 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full -translate-y-16 translate-x-16"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-indigo-400/20 to-blue-400/20 rounded-full translate-y-12 -translate-x-12"></div>
      
      <div className="relative flex items-start justify-between">
        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {getGreeting()}, {getUserDisplayName()}! 👋
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-md">
            Welcome to your {getUserRoleDisplay()} dashboard. Here's what's happening today.
          </p>
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm">
              <span className="text-blue-600 dark:text-blue-400">📅</span>
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
            <Badge variant="outline" className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm font-medium">
              {getUserRoleDisplay()}
            </Badge>
          </div>
        </div>
        <div className="text-5xl opacity-80">🎯</div>
      </div>
    </div>
  );
};

const baseData = Array.from({ length: 90 }).map((_, i) => ({ x: i, y: Math.floor(Math.random()*30)+5 }));

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { data: stats, isLoading } = useQuery({ 
    queryKey: ['admin-stats'], 
    queryFn: adminService.getDashboardStats 
  });
  
  const { data: orders } = useQuery({ 
    queryKey: ['recent-orders'], 
    queryFn: () => ordersService.getAllOrders(1, 10)
  });

  const [timeframe, setTimeframe] = useState<'3m'|'30d'|'7d'>('3m');
  const chartData = useMemo(() => {
    const len = timeframe === '3m' ? 90 : timeframe === '30d' ? 30 : 7;
    const sliced = baseData.slice(-len);
    return sliced.map((p, idx) => ({ day: timeframe === '7d' ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][idx % 7] : `Day ${idx+1}`, bookings: p.y }));
  }, [timeframe]);

  const cards = [
    { title: 'Total Revenue', value: isLoading ? '...' : `$${stats?.totalRevenue ?? 0}`, sub: 'Trending up this month', trend: '+12.5%', icon: '💰', color: '#16a34a' },
    { title: 'Total Users', value: isLoading ? '...' : stats?.totalUsers ?? 0, sub: 'Acquisition needs attention', trend: '-20%', icon: '🧑‍💼', color: '#ef4444' },
    { title: 'Total Partners', value: isLoading ? '...' : stats?.totalPartners ?? 0, sub: 'Strong partner growth', trend: '+12.5%', icon: '🏢', color: '#6366f1' },
    { title: 'Total Orders', value: isLoading ? '...' : stats?.totalOrders ?? 0, sub: 'Meets projections', trend: '+4.5%', icon: '🧾', color: '#f59e0b' },
  ];

  const recentOrders = orders?.data || [];

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <WelcomeBanner user={user} />
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((s, i) => (<StatCard key={i} {...s} />))}
      </div>

      {/* Chart Section */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="text-lg font-medium">Total Visitors</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total for the selected period</p>
          </div>
          <Segmented value={timeframe} onChange={(v)=>setTimeframe(v as any)} options={[{value:'3m',label:'Last 3 months'},{value:'30d',label:'Last 30 days'},{value:'7d',label:'Last 7 days'}]} />
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ left: -16, right: 8, top: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="day" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip contentStyle={{ backgroundColor: '#111827', color: '#fff', border: 'none' }} labelStyle={{ color: '#fff' }} />
              <Line type="monotone" dataKey="bookings" stroke="#6366f1" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Orders Table */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h3 className="text-lg font-medium">Recent Orders</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Latest 10 entries</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline">Customize Columns</Button>
            <Button>Add Order</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b border-gray-200 dark:border-gray-800">
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Partner</th>
                <th className="px-3 py-2">Total</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.length > 0 ? (
                recentOrders.map((order: any) => (
                  <tr key={order.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-3 py-2 font-mono text-xs">#{order.id}</td>
                    <td className="px-3 py-2">Customer {order.customerId}</td>
                    <td className="px-3 py-2">Partner {order.partnerId}</td>
                    <td className="px-3 py-2 font-medium">${order.totalAmount}</td>
                    <td className="px-3 py-2">
                      <Badge variant={order.status === 'completed' ? 'default' : 'secondary'}>
                        {order.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-gray-500 dark:text-gray-400">
                    {isLoading ? 'Loading orders...' : 'No orders found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
